import { splitProps } from "solid-js";
import type { IconProps } from "~/types/icon";

export type ChevronDirection = "up" | "down" | "left" | "right";

export interface ChevronIconProps extends Omit<IconProps, "direction"> {
  direction?: ChevronDirection;
}

/** SVG path data for each direction */
const PATHS: Record<ChevronDirection, string> = {
  up: "M3.25 10.5L8.25 5.5L13.25 10.5",
  down: "M13.25 5.5L8.25 10.5L3.25 5.5",
  left: "M10.5 13.25L5.5 8.25L10.5 3.25",
  right: "M5.5 3.25L10.5 8.25L5.5 13.25",
};

/** ViewBox dimensions differ for vertical vs horizontal orientations */
const VIEWBOXES: Record<ChevronDirection, { width: number; height: number; viewBox: string }> = {
  up: { width: 17, height: 16, viewBox: "0 0 17 16" },
  down: { width: 17, height: 16, viewBox: "0 0 17 16" },
  left: { width: 16, height: 17, viewBox: "0 0 16 17" },
  right: { width: 16, height: 17, viewBox: "0 0 16 17" },
};

/**
 * Unified chevron icon with direction support.
 *
 * @example
 * <ChevronIcon direction="down" />
 * <ChevronIcon direction="up" class={styles.icon} />
 */
export default function ChevronIcon(props: ChevronIconProps) {
  const [local, others] = splitProps(props, ["class", "classList", "direction"]);
  const direction = () => local.direction ?? "down";
  const dimensions = () => VIEWBOXES[direction()];
  const path = () => PATHS[direction()];

  return (
    <svg
      class={local.class}
      classList={local.classList}
      xmlns="http://www.w3.org/2000/svg"
      width={dimensions().width}
      height={dimensions().height}
      viewBox={dimensions().viewBox}
      fill="none"
      {...others}
    >
      <path
        d={path()}
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}
